/**
 * Data layer for `igaming-brand-level-reporting-isolation`. Thin typed fetch wrappers over /api/v1/igaming-brand-level-reporting-isolation.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingBrandLevelReportingIsolationRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingBrandLevelReportingIsolationResponse {
	count: number;
	items: IgamingBrandLevelReportingIsolationRow[];
}

export interface CreateIgamingBrandLevelReportingIsolationBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-brand-level-reporting-isolation";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingBrandLevelReportingIsolationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingBrandLevelReportingIsolationResponse>(res);
}

export async function create(body: CreateIgamingBrandLevelReportingIsolationBody): Promise<IgamingBrandLevelReportingIsolationRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingBrandLevelReportingIsolationRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingBrandLevelReportingIsolationQueryKeys = {
	all: ["igaming-brand-level-reporting-isolation"] as const,
	list: () => [...igamingBrandLevelReportingIsolationQueryKeys.all] as const,
	detail: (id: string) => [...igamingBrandLevelReportingIsolationQueryKeys.all, id] as const,
};
